/**
 * 云闪付 upsdk 就绪门控：
 * - 云闪付小程序（`IS_IN_UP_APP`）：`upsdk.pluginReady`
 * - 云网（`IS_IN_UP_WEB`）：`postUpSdkAuthInfo({ url: window.location.href })` → `upsdk.config` → `upsdk.ready` / `upsdk.error`
 *
 * 云网 config 与官方 demo 对齐：传完整 `window.location.href`，接口成功后直接 config。
 */
import { postUpSdkAuthInfo, type IUpSdkAuthInfo } from '@/service/index/up-sdk-auth'

// --- 类型定义 ---

interface UpsdkCloudWebConfigOptions {
  appId: string
  timestamp: number | string
  nonceStr: string
  signature: string
  debug?: boolean
}

interface UpsdkCloudWebClient {
  config?: (options: UpsdkCloudWebConfigOptions) => void
  ready?: (callback: () => void) => void
  error?: (callback: (err: unknown) => void) => void
  pluginReady?: (callback: () => void) => void
}

/** 云网 upsdk 排障上下文（config 失败时随 Error 一并输出） */
interface UpsdkCloudWebConfigContext {
  apiName?: string
  pageUrl: string
  authInfo?: IUpSdkAuthInfo
}

/** `runWhenUpsdkReady` 可选参数 */
export interface RunWhenUpsdkReadyOptions {
  /** 触发本次 upsdk 调用的 API 名，便于云网 config 失败时写入 Error 便于排查 */
  apiName?: string
}

// --- 常量与变量 ---

/** 当前 H5 文档内 upsdk.config 就绪 Promise；SPA 路由切换复用，整页 reload 后自动重置 */
let cloudWebUpsdkReadyPromise: Promise<void> | null = null
/** 当前 H5 文档内 upsdk.config 已成功 */
let cloudWebUpsdkConfiguredOk = false
/** 当前文档内 config 已失败；避免同文档内重复 POST */
let cloudWebUpsdkConfigFailedPromise: Promise<void> | null = null
let cloudWebConfigAttemptContext: UpsdkCloudWebConfigContext | null = null

/** uni + sessionStorage：标记第三方支付整页外跳 */
const CLOUD_WEB_EXTERNAL_NAV_PENDING_KEY = 'travel_cloud_web_external_nav_pending'

function readCloudWebUpsdkStorage(key: string): string {
  try {
    const stored = uni.getStorageSync(key)
    if (typeof stored === 'string' && stored !== '') {
      return stored
    }
  }
  catch {
    /* empty */
  }
  if (typeof sessionStorage === 'undefined') {
    return ''
  }
  try {
    const sessionValue = sessionStorage.getItem(key)
    return sessionValue ?? ''
  }
  catch {
    return ''
  }
}

function writeCloudWebUpsdkStorage(key: string, value: string): void {
  try {
    uni.setStorageSync(key, value)
  }
  catch {
    /* empty */
  }
  if (typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.setItem(key, value)
  }
  catch {
    /* empty */
  }
}

function clearCloudWebUpsdkStorageKey(key: string): void {
  try {
    uni.removeStorageSync(key)
  }
  catch {
    /* empty */
  }
  if (typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.removeItem(key)
  }
  catch {
    /* empty */
  }
}

/** 支付等整页外跳前调用（{@link markCloudWebPaymentExternalNavPending}） */
export function markCloudWebPaymentExternalNavPending(): void {
  writeCloudWebUpsdkStorage(CLOUD_WEB_EXTERNAL_NAV_PENDING_KEY, '1')
}

function resetCloudWebUpsdkConfigStateForRetry(): void {
  cloudWebUpsdkReadyPromise = null
  cloudWebUpsdkConfiguredOk = false
  cloudWebUpsdkConfigFailedPromise = null
  cloudWebConfigAttemptContext = null
}

/**
 * 云网 upsdk bootstrap：冷启动/第三方回跳均尽早 config 一次。
 */
export function prepareCloudWebUpsdkBootstrap(): void {
  if (typeof window === 'undefined' || !isUpsdkCloudWebHost()) {
    return
  }

  const paymentReturn = readCloudWebUpsdkStorage(CLOUD_WEB_EXTERNAL_NAV_PENDING_KEY) === '1'
  clearCloudWebUpsdkStorageKey(CLOUD_WEB_EXTERNAL_NAV_PENDING_KEY)

  if (paymentReturn) {
    resetCloudWebUpsdkConfigStateForRetry()
  }

  void ensureCloudWebUpsdkReady({
    apiName: paymentReturn
      ? 'bootstrap.prepareCloudWebUpsdkBootstrap.paymentReturn'
      : 'bootstrap.prepareCloudWebUpsdkBootstrap',
  }).catch(() => {
    /* config 成败日志在 ensureCloudWebUpsdkReady 内统一输出 */
  })
}

function markCloudWebUpsdkConfiguredForCurrentDocument(): void {
  cloudWebUpsdkConfiguredOk = true
  cloudWebUpsdkConfigFailedPromise = null
}

// --- 函数 ---

function isUpsdkMiniProgramHost(): boolean {
  return globalThis.IS_IN_UP_APP === true
}

function isUpsdkCloudWebHost(): boolean {
  return globalThis.IS_IN_UP_WEB === true
}

function readCloudWebUpsdkPageUrl(): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.location.href
}

function validateUpSdkAuthInfo(authInfo: IUpSdkAuthInfo | undefined): authInfo is IUpSdkAuthInfo {
  if (!authInfo) {
    return false
  }
  const appId = String(authInfo.appId ?? '').trim()
  const nonceStr = String(authInfo.nonceStr ?? '').trim()
  const signature = String(authInfo.signature ?? '').trim()
  const timestamp = String(authInfo.timestamp ?? '').trim()
  return appId !== '' && nonceStr !== '' && signature !== '' && timestamp !== ''
}

function cloneAuthInfoForDiagnostic(authInfo: IUpSdkAuthInfo): IUpSdkAuthInfo {
  return {
    appId: String(authInfo.appId ?? '').trim(),
    nonceStr: String(authInfo.nonceStr ?? '').trim(),
    signature: String(authInfo.signature ?? '').trim(),
    timestamp: String(authInfo.timestamp ?? '').trim(),
  }
}

function formatRawUpsdkError(rawError: unknown): string {
  if (rawError instanceof Error) {
    return rawError.message
  }
  if (typeof rawError === 'string') {
    return rawError
  }
  try {
    return JSON.stringify(rawError)
  }
  catch {
    return String(rawError)
  }
}

function createUpsdkCloudWebError(
  stage: string,
  context: UpsdkCloudWebConfigContext,
  rawError?: unknown,
): Error {
  const messageParts = [
    `[云网 upsdk] ${stage}`,
    context.apiName ? `api=${context.apiName}` : '',
    `page=${context.pageUrl}`,
    context.authInfo
      ? `authInfo=${JSON.stringify(cloneAuthInfoForDiagnostic(context.authInfo))}`
      : '',
    rawError !== undefined ? `raw=${formatRawUpsdkError(rawError)}` : '',
  ].filter(part => part !== '')

  const error = new Error(messageParts.join(' | '))
  error.name = 'UpsdkCloudWebError'
  Object.assign(error, {
    stage,
    apiName: context.apiName,
    pageUrl: context.pageUrl,
    authInfo: context.authInfo ? cloneAuthInfoForDiagnostic(context.authInfo) : undefined,
    rawError,
  })
  return error
}

function buildCloudWebConfigContext(
  options?: RunWhenUpsdkReadyOptions,
): UpsdkCloudWebConfigContext {
  return {
    apiName: options?.apiName,
    pageUrl: readCloudWebUpsdkPageUrl(),
  }
}

function mergeCloudWebConfigApiName(apiName?: string): void {
  if (!apiName || !cloudWebConfigAttemptContext) {
    return
  }
  cloudWebConfigAttemptContext.apiName = apiName
}

function isUpsdkCloudWebError(error: unknown): error is Error & UpsdkCloudWebConfigContext & { stage: string } {
  return error instanceof Error && error.name === 'UpsdkCloudWebError'
}

function invokeUpsdkCloudWebConfig(
  authInfo: IUpSdkAuthInfo,
  context: UpsdkCloudWebConfigContext,
): Promise<void> {
  if (typeof upsdk === 'undefined') {
    return Promise.reject(new Error('upsdk 未加载'))
  }

  const sdk = upsdk as UpsdkCloudWebClient
  if (typeof sdk.config !== 'function') {
    return Promise.reject(new Error('upsdk.config 不可用'))
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false
    const settleOnce = (settle: () => void) => {
      if (settled) {
        return
      }
      settled = true
      settle()
    }

    const configContext: UpsdkCloudWebConfigContext = {
      ...context,
      authInfo: cloneAuthInfoForDiagnostic(authInfo),
    }
    cloudWebConfigAttemptContext = configContext

    console.log('[upsdk.config] window.location.href:', readCloudWebUpsdkPageUrl())

    sdk.error?.((error: unknown) => {
      settleOnce(() => {
        reject(createUpsdkCloudWebError('upsdk.config', configContext, error))
      })
    })

    sdk.ready?.(() => {
      console.log('[upsdk.config] 成功')
      markCloudWebUpsdkConfiguredForCurrentDocument()
      settleOnce(() => resolve())
    })

    sdk.config({
      appId: authInfo.appId,
      timestamp: authInfo.timestamp,
      nonceStr: authInfo.nonceStr,
      signature: authInfo.signature,
      debug: false,
    })
  })
}

function ensureCloudWebUpsdkReady(options?: RunWhenUpsdkReadyOptions): Promise<void> {
  if (cloudWebUpsdkConfiguredOk) {
    return Promise.resolve()
  }

  if (cloudWebUpsdkReadyPromise) {
    mergeCloudWebConfigApiName(options?.apiName)
    return cloudWebUpsdkReadyPromise
  }

  if (cloudWebUpsdkConfigFailedPromise) {
    return cloudWebUpsdkConfigFailedPromise
  }

  const apiUrl = readCloudWebUpsdkPageUrl()
  if (apiUrl === '') {
    return Promise.reject(new Error('云网 upsdk config 缺少 page url'))
  }

  const configContext = buildCloudWebConfigContext(options)
  cloudWebConfigAttemptContext = configContext

  console.log('[upsdk.config] 传递给接口的 url:', apiUrl)

  cloudWebUpsdkReadyPromise = postUpSdkAuthInfo({ url: apiUrl }, { hideErrorToast: true })
    .then((authInfo) => {
      if (!validateUpSdkAuthInfo(authInfo)) {
        throw createUpsdkCloudWebError('upSdkAuthInfo.invalid', configContext, authInfo)
      }
      return invokeUpsdkCloudWebConfig(authInfo, configContext)
    })
    .catch((error) => {
      console.log('[upsdk.config] 失败', error instanceof Error ? error.message : String(error))
      cloudWebUpsdkReadyPromise = null
      const normalizedError = isUpsdkCloudWebError(error)
        ? error
        : createUpsdkCloudWebError('upSdkAuthInfo.request', configContext, error)
      cloudWebUpsdkConfigFailedPromise = Promise.reject(normalizedError)
      throw normalizedError
    })

  return cloudWebUpsdkReadyPromise
}

function runWhenUpsdkPluginReady(task: () => void | Promise<void>): Promise<void> {
  if (typeof upsdk === 'undefined') {
    return Promise.reject(new Error('upsdk 未加载'))
  }

  return new Promise<void>((resolve, reject) => {
    const runTask = () => {
      void Promise.resolve(task()).then(resolve).catch(reject)
    }

    if (typeof upsdk.pluginReady === 'function') {
      upsdk.pluginReady(runTask)
      return
    }

    runTask()
  })
}

/**
 * upsdk 就绪后执行任务。
 * 云闪付小程序走 `pluginReady`；云网走 config + ready（当前 H5 文档内仅 config 一次）。
 */
export function runWhenUpsdkReady(
  task: () => void | Promise<void>,
  options?: RunWhenUpsdkReadyOptions,
): Promise<void> {
  if (isUpsdkMiniProgramHost()) {
    return runWhenUpsdkPluginReady(task)
  }

  if (isUpsdkCloudWebHost()) {
    return ensureCloudWebUpsdkReady(options)
      .then(() => Promise.resolve(task()))
      .catch((error) => {
        if (isUpsdkCloudWebError(error)) {
          throw error
        }
        const context = cloudWebConfigAttemptContext ?? buildCloudWebConfigContext(options)
        throw createUpsdkCloudWebError('task', context, error)
      })
  }

  return Promise.reject(new Error('非 upsdk 宿主环境'))
}
